import * as amplitude from '@amplitude/analytics-browser';

import { SnowplowService } from 'wanderu-service-analytics';
import { getBreakpoint } from 'wanderu-utils';

function searchResultsLoaded({
    departDate,
    destinationPlaceId,
    destinationText,
    originPlaceId,
    originText,
    returnDate,
}) {
    const property = {
        departDate: departDate,
        destination: {
            address: destinationText,
            placeId: destinationPlaceId,
        },
        origin: {
            address: originText,
            placeId: originPlaceId,
        },
        returnDate: returnDate || '',
    };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'loaded',
        category: 'react_search',
        label: 'trip_results',
        property,
    });
}

function firstRendered({
    departDate,
    destinationPlaceId,
    destinationText,
    originPlaceId,
    originText,
    returnDate,
}) {
    const property = {
        departDate: departDate,
        destination: {
            address: destinationText,
            placeId: destinationPlaceId,
        },
        origin: {
            address: originText,
            placeId: originPlaceId,
        },
        returnDate: returnDate,
    };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'first_batch_rendered',
        category: 'react_search',
        label: 'trip_results',
        property,
    });
}

function seeMoreButtonClicked({
    batchResultCount,
    direction,
    funnelStage,
    searchParams = {},
    visibleResultsCountAfter,
    visibleResultsCountBefore,
}) {
    const {
        destinationPlaceId,
        destinationText,
        originPlaceId,
        originText,
    } = searchParams;

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: 'react_search',
        label: 'more_results',
    });

    amplitude.track('search_interacted', {
        arrive_address: destinationText,
        arrive_place_id: destinationPlaceId,
        batch_result_count: batchResultCount,
        breakpoint: getBreakpoint(),
        depart_address: originText,
        depart_place_id: originPlaceId,
        funnel_stage: funnelStage,
        interaction_type: 'view_more_results',
        is_departing_results: direction === 'depart',
        visible_results_count_after: visibleResultsCountAfter,
        visible_results_count_before: visibleResultsCountBefore,
    });
}

function resetAllFiltersClicked({
    direction,
    funnelStage,
    isAllowNearbyFiltered,
    isArrivalStationsFiltered,
    isArrivalTimingFiltered,
    isDepartureStationsFiltered,
    isDepartureTimingFiltered,
    isDirectOnlyFiltered,
    isFeaturesFiltered,
    isTravelModesFiltered,
    searchParams = {},
}) {
    const {
        destinationPlaceId,
        destinationText,
        originPlaceId,
        originText,
    } = searchParams;

    const filters = [];

    if (isAllowNearbyFiltered) {
        filters.push('nearby');
    }
    if (isArrivalStationsFiltered || isDepartureStationsFiltered) {
        filters.push('stations');
    }
    if (isArrivalTimingFiltered || isDepartureTimingFiltered) {
        filters.push('time');
    }
    if (isDirectOnlyFiltered) {
        filters.push('transfers');
    }
    if (isFeaturesFiltered) {
        filters.push('amenities');
    }
    if (isTravelModesFiltered) {
        filters.push('travel_mode_or_carriers');
    }

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: 'react_search',
        label: 'reset_all_filters',
    });

    amplitude.track('search_interacted', {
        arrive_address: destinationText,
        arrive_place_id: destinationPlaceId,
        breakpoint: getBreakpoint(),
        depart_address: originText,
        depart_place_id: originPlaceId,
        filters_reset: filters,
        funnel_stage: funnelStage,
        interaction_type: 'reset_all_filters',
        is_departing_results: direction === 'depart',
    });
}

function tripSelected(data) {
    const {
        activeSortType,
        batchId,
        direction,
        position,
        price,
        resultsCount,
        resultsVisible,
        trip: {
            arriveId,
            carrierCode,
            departId,
            isRouted,
            tripLegs,
            _priceInUSDForSorting,
        },
        tripId,
    } = data;

    const property = {
        batch_id: batchId,
        position,
        price,
        results_count: resultsCount,
        results_visible: resultsVisible,
        search_sort_name: activeSortType,
        trip_id: tripId,
    };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'select_result',
        category: 'react_search',
        label: direction,
        property,
    });

    amplitude.track('trip_selected', {
        arrive_station_id: arriveId,
        batch_id: batchId,
        breakpoint: getBreakpoint(),
        carrier_code: carrierCode,
        depart_station_id: departId,
        is_departing_results: direction === 'depart',
        is_routed: isRouted,
        leg_count: tripLegs?.length,
        results_count: resultsCount,
        results_position: position + 1,
        search_sort_name: activeSortType,
        trip_id: tripId,
        usd_price: _priceInUSDForSorting || price,
    });
}

function dateSuggestionSelected(data) {
    const {
        departDate,
        destinationPlaceId,
        destinationText,
        originPlaceId,
        originText,
        suggestionDate,
    } = data;

    const property = {
        date_selected: suggestionDate,
        destination: {
            address: destinationText,
            placeId: destinationPlaceId,
        },
        origin: {
            address: originText,
            placeId: originPlaceId,
        },
        search_date: departDate,
    };

    SnowplowService.fireStructuredEventWithBatchId({
        action: 'click',
        category: 'react_search',
        label: 'date_suggestion',
        property,
    });

    amplitude.track('date_suggestion clicked', property);
}

export default {
    dateSuggestionSelected,
    firstRendered,
    resetAllFiltersClicked,
    searchResultsLoaded,
    seeMoreButtonClicked,
    tripSelected,
};
